import React from 'react';
import PropTypes from 'prop-types';

const READER_STATE_CONFIG = {
  // Idle / Normal States (Green dot)
  IDLE: { label: 'En línea', dotClass: 'dot-green', isOffline: false },
  CLOSE_DOOR: { label: 'En línea', dotClass: 'dot-green', isOffline: false },
  DOOR_CLOSED: { label: 'En línea', dotClass: 'dot-green', isOffline: false },
  CLOSED: { label: 'En línea', dotClass: 'dot-green', isOffline: false },

  // Active Access / Open Door States
  OPEN_DOOR: { label: 'Abierta', dotClass: 'dot-green', isOffline: false },
  DOOR_OPEN: { label: 'Abierta', dotClass: 'dot-green', isOffline: false },
  OPEN: { label: 'Abierta', dotClass: 'dot-green', isOffline: false },
  ACCESS_GRANTED: { label: 'Acceso concedido', dotClass: 'dot-green', isOffline: false },
  UNLOCKED: { label: 'Desbloqueada', dotClass: 'dot-green', isOffline: false },
  DOOR_BOUNCE_GRACE_MS: { label: 'En línea', dotClass: 'dot-green', isOffline: false },

  // Card Authentication Flow
  ACCESS_CARD_READ: { label: 'Validando', dotClass: 'dot-blue', isOffline: false },
  AUTH_PENDING: { label: 'Validando', dotClass: 'dot-blue', isOffline: false },
  BOOTING: { label: 'Iniciando', dotClass: 'dot-blue', isOffline: false },
  REGISTRATION_MODE: { label: 'Registro', dotClass: 'dot-purple', isOffline: false },

  // Configuration & Warnings
  CONFIGURATION_MODE: { label: 'Configuración', dotClass: 'dot-yellow', isOffline: false },
  DOOR_HELD_WARNING: { label: 'Puerta retenida', dotClass: 'dot-yellow', isOffline: false },
  SECURITY_DOOR_HELD: { label: 'Puerta retenida', dotClass: 'dot-yellow', isOffline: false },
  HELD_OPEN: { label: 'Puerta retenida', dotClass: 'dot-yellow', isOffline: false },

  // Alarms & Emergency States (Red dot)
  EVACUATION_MODE: { label: 'Emergencia', dotClass: 'dot-red', isOffline: false },
  EMERGENCY: { label: 'Emergencia', dotClass: 'dot-red', isOffline: false },
  SECURITY_EMERGENCY_ACTIVATED: { label: 'Emergencia', dotClass: 'dot-red', isOffline: false },
  INTRUDER_ALERT: { label: 'Intrusión', dotClass: 'dot-red', isOffline: false },
  FORCED_OPEN: { label: 'Puerta forzada', dotClass: 'dot-red', isOffline: false },
  SECURITY_DOOR_FORCED: { label: 'Puerta forzada', dotClass: 'dot-red', isOffline: false },
  ACCESS_LOCKED: { label: 'Bloqueada', dotClass: 'dot-red', isOffline: false },
  LOCKED: { label: 'Bloqueada', dotClass: 'dot-red', isOffline: false },
  ACCESS_DENIED: { label: 'Acceso denegado', dotClass: 'dot-red', isOffline: false },

  // Disconnected / Offline States (Red dot, isOffline: true)
  READER_DISCONNECTED: { label: 'Desconectada', dotClass: 'dot-red', isOffline: true },
  SERVER_DISCONNECTED: { label: 'Desconectada', dotClass: 'dot-red', isOffline: true },
  BROKEN_SLAVE: { label: 'Desconectada', dotClass: 'dot-red', isOffline: true },
};

const getStatusConfig = (status) => {
  if (!status || String(status).trim() === '') {
    return { label: 'Desconectada', dotClass: 'dot-red', isOffline: true };
  }
  const key = String(status).toUpperCase();
  if (READER_STATE_CONFIG[key]) return READER_STATE_CONFIG[key];

  if (key.includes('EMERGENCY') || key.includes('EVACUAT')) {
    return { label: 'Emergencia', dotClass: 'dot-red', isOffline: false };
  }
  if (key.includes('INTRUS') || key.includes('FORCED')) {
    return { label: 'Intrusión', dotClass: 'dot-red', isOffline: false };
  }
  if (key.includes('DISCONNECT') || key.includes('BROKEN') || key.includes('OFFLINE')) {
    return { label: 'Desconectada', dotClass: 'dot-red', isOffline: true };
  }

  return { label: 'En línea', dotClass: 'dot-green', isOffline: false };
};

const ReaderStatusIndicator = ({ label, status, spiOk, deviceType }) => {
  const config = getStatusConfig(status);
  const isOffline = config.isOffline;

  const statusBadge = (
    <span className="status-item-fixed">
      <span className={`status-dot ${config.dotClass}`} />
      <span className="status-text">{config.label}</span>
    </span>
  );

  const isSpiActive = !isOffline && spiOk === true;
  const spiText = isSpiActive ? 'OK' : 'Error';
  const spiTitle = `Bus SPI: ${spiText}`;
  const spiDotClass = isSpiActive ? 'dot-green' : 'dot-red';

  const spiBadge = (
    <span className="status-item-fixed spi-compact-badge" title={spiTitle}>
      <span className="spi-label-text">SPI:</span>
      <span className={`status-dot ${spiDotClass}`} />
      <span className="spi-text-value">{spiText}</span>
    </span>
  );

  const typeLabel = deviceType ? (deviceType === 'vehicular' ? 'Vehicular' : 'Peatonal') : null;
  const ariaDescription = `${label}${typeLabel ? ` (${typeLabel})` : ''}: ${config.label}, ${spiTitle}`;

  return (
    <div className="telemetry-badge-container" role="group" aria-label={ariaDescription}>
      <div className="telemetry-role-header">{label}</div>
      <div className="telemetry-badge-row">
        {statusBadge}
        <span className="telemetry-spi-wrapper">{spiBadge}</span>
      </div>
    </div>
  );
};

ReaderStatusIndicator.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.string,
  spiOk: PropTypes.bool,
  deviceType: PropTypes.string,
};

export default ReaderStatusIndicator;
